import { UserManagerSettings } from 'oidc-client-ts';
import { appConfig } from './appConfig';

// ---------------------------------------------------------------------------
// OIDC Discovery Document Cache
// ---------------------------------------------------------------------------
// WHY: The HAR shows fetching .well-known/openid-configuration takes 2,338 ms
// per user (1,190 ms TCP+TLS + 895 ms TLS handshake + 300 ms server wait).
// oidc-client-ts fetches this fresh on every signinRedirect() call.
// At 650 simultaneous logins that is 650 cold connections to uat-fedsvc —
// the same TLS storm pattern that caused the original timeout on Azure AD.
//
// FIX: Fetch the discovery document ONCE at module load time and store it in
// a module-level promise.  Every subsequent call awaits the same promise —
// zero extra network calls regardless of how many users log in concurrently.
//
// TTL: The discovery document changes only when IQVIA rotates endpoints
// (very rare).  We use sessionStorage so it survives React re-renders but
// is cleared when the browser tab closes — safe, no stale-config risk.
//
// Saving: ~2,338 ms per user removed from the login journey (24% of total).
// ---------------------------------------------------------------------------

const DISCOVERY_CACHE_KEY = 'oidc_discovery_cache';
const DISCOVERY_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

interface CachedDiscovery {
  metadata: Record<string, unknown>;
  fetchedAt: number;
}

function getDiscoveryFromSessionStorage(): Record<string, unknown> | null {
  try {
    const raw = sessionStorage.getItem(DISCOVERY_CACHE_KEY);
    if (!raw) return null;
    const cached: CachedDiscovery = JSON.parse(raw);
    if (Date.now() - cached.fetchedAt > DISCOVERY_CACHE_TTL_MS) {
      sessionStorage.removeItem(DISCOVERY_CACHE_KEY);
      return null;
    }
    return cached.metadata;
  } catch {
    return null;
  }
}

// Single shared fetch promise — all concurrent callers await the same request.
let _discoveryPromise: Promise<Record<string, unknown> | null> | null = null;

function prefetchOidcDiscovery(): Promise<Record<string, unknown> | null> {
  // Return cached value immediately if still fresh
  const cached = getDiscoveryFromSessionStorage();
  if (cached) return Promise.resolve(cached);

  // Return the in-flight promise if a fetch is already in progress
  if (_discoveryPromise) return _discoveryPromise;

  const discoveryUrl = `${appConfig.APP_OIDC_AUTHORITY}/.well-known/openid-configuration`;

  _discoveryPromise = fetch(discoveryUrl)
    .then((res) => {
      if (!res.ok) throw new Error(`Discovery fetch failed: ${res.status}`);
      return res.json();
    })
    .then((metadata: Record<string, unknown>) => {
      try {
        const entry: CachedDiscovery = { metadata, fetchedAt: Date.now() };
        sessionStorage.setItem(DISCOVERY_CACHE_KEY, JSON.stringify(entry));
      } catch {
        // sessionStorage full — proceed without caching, not a fatal error
      }
      _discoveryPromise = null; // allow refresh after TTL
      return metadata;
    })
    .catch((err) => {
      console.warn('[oidcConfig] Discovery prefetch failed, oidc-client-ts will fetch on demand:', err);
      _discoveryPromise = null;
      return null; // non-fatal — oidc-client-ts falls back to fetching itself
    });

  return _discoveryPromise;
}

// Kick off the fetch immediately at module load time so it is warm
// before the user clicks Login.  The result is stored in sessionStorage.
prefetchOidcDiscovery();

// Build metadata object synchronously from sessionStorage if already cached,
// otherwise leave undefined so oidc-client-ts fetches it (first-ever load).
const _cachedMetadata = getDiscoveryFromSessionStorage() ?? undefined;

export const oidcConfig: UserManagerSettings = {
  authority: appConfig.APP_OIDC_AUTHORITY,
  client_id: appConfig.APP_OIDC_CLIENT_ID,
  redirect_uri: `${window.location.origin}${appConfig.APP_HOME_URL}`,
  post_logout_redirect_uri: `${window.location.origin}${appConfig.APP_HOME_URL}`,
  response_type: 'code',
  scope: appConfig.APP_OIDC_SCOPE,
  automaticSilentRenew: false,
  loadUserInfo: false,
  monitorSession: false,
  // Pass pre-fetched metadata so oidc-client-ts skips the discovery network call.
  // On the very first load _cachedMetadata is undefined — oidc-client-ts fetches
  // normally, stores result, and every subsequent load (including all 650 users
  // after the first) uses the sessionStorage cache via prefetchOidcDiscovery().
  metadata: _cachedMetadata as UserManagerSettings['metadata'],
};